import React, { useCallback, useEffect, useRef, useState } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { isEmpty, isNil, pathOr } from 'ramda';
import NumberFlow from '@number-flow/react';

// api
import UserService from '../../service/user-service';
import AssignedApplicationService from '../../service/assigned-application-service';
import DashboardService from '../../service/dashboard-service';

// store
import { storeSet } from '../../store';

// tools
import set404FromErrorResponse from '../../helpers/set404FromErrorResponse';
import uniqid from '../../helpers/uniqid';

// components
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { Dropdown } from 'primereact/dropdown';
import AllDomandeTableAsync from './components/AllDomandeTableAsync';
import { InputText } from 'primereact/inputtext';
import { isCodiceFiscale, isEmail, isPIVA } from '../../helpers/validators';
import { klona } from 'klona';
import { useDebounce } from 'primereact/hooks';
import CompanyService from '../../service/company-service';
import { Messages } from 'primereact/messages';
import ChipsStandalone from '../../components/ChipsStandalone';
import { Editor } from 'primereact/editor';
import { Toast } from 'primereact/toast';

const Domande = () => {
    const [loading, setLoading] = useState(false);
    const [isVisibleEditDialog, setIsVisibleEditDialog] = useState(false);
    const [roleIds, setRoleIds] = useState([]);
    const [users, setUsers] = useState([]);
    const [chosenUser, setChosenUser] = useState(0);
    const [chosenApplication, setChosenApplication] = useState(0);
    const [updaterString, setUpdaterString] = useState('');
    const [mainStats, setMainStats] = useState({});
    const [isVisibleInvitationDialog, setIsVisibleInvitationDialog] = useState(false);
    const [newUserData, setNewUserData] = useState({});
    const [, debouncedPivaValue, setInputPiva] = useDebounce('', 1000);
    const [isVatValid, setIsVatValid] = useState(true);
    const [vatCheckResponse, setVatCheckResponse] = useState({});
    const toast = useRef(null);
    const errorMsgs = useRef(null);

    const getRolesCallback = (data) => {
        if (data.status === 'SUCCESS') {
            const roles = data.data
                .filter(o => ['ROLE_PRE_INSTRUCTOR', 'ROLE_INSTRUCTOR_MANAGER'].includes(o.roleType));
            setRoleIds(roles.map(o => o.id));
        }
        setLoading(false);
    }

    const errGetRolesCallback = (data) => {
        set404FromErrorResponse(data);
        setLoading(false);
    }

    const getUsersCallback = (data) => {
        if (data.status === 'SUCCESS') {
            const users = data.data
                .map(o => ({
                    name: `${o.firstName} ${o.lastName} (${o.email})`,
                    value: o.id
                }));
            setUsers(users);
        }
        setLoading(false);
    }

    const errGetUsersCallback = (data) => {
        set404FromErrorResponse(data);
        setLoading(false);
    }

    const headerEditDialog = () => {
        return <span>{__('Assegni la domanda', 'htr')}</span>
    }

    const hideEditDialog = () => {
        setIsVisibleEditDialog(false);
        setChosenUser(0);
        setChosenApplication(0);
    }

    const footerEditDialog = () => {
        return <div>
            <Button type="button" label={__('Annulla', 'htr')} onClick={hideEditDialog} outlined/>
            <Button
                type="button"
                disabled={loading}
                label={__('Invia', 'htr')} onClick={saveEditDialog}/>
        </div>
    }

    const openAssignDialog = (applId) => {
        setChosenApplication(applId)
        setIsVisibleEditDialog(true);
    }

    const saveEditDialog = () => {
        if (chosenUser !== 0 && chosenApplication !== 0) {
            storeSet('setAsyncRequest');
            AssignedApplicationService.assignApplication(chosenApplication, assignApplCallback, errAssignApplCallback, [
                ['userId', chosenUser]
            ]);
            hideEditDialog();
        }
    }

    const assignApplCallback = (data) => {
        if (data.status === 'SUCCESS') {
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: data.message
                });
            }
            setUpdaterString(uniqid());
        }
        storeSet('unsetAsyncRequest');
    }

    const errAssignApplCallback = (data) => {
        if (toast.current && data.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: data.message
            });
        }
        set404FromErrorResponse(data);
        storeSet('unsetAsyncRequest');
    }

    const openInvitationDialog = () => {
        setNewUserData({});
        setIsVisibleInvitationDialog(true);
    }

    const headerInvitationDialog = () => {
        return <span>{__('Invita Nuovo Fornitore', 'htr')}</span>
    }

    const hideInvitationDialog = () => {
        setIsVisibleInvitationDialog(false);
        setNewUserData({});
        setInputPiva('');
    }

    const footerInvitationDialog = useCallback(() => {
        return <div>
            <Button
                type="button"
                label={__('Annulla', 'htr')}
                onClick={hideInvitationDialog}
                icon="pi pi-times"
                iconPos="right"
                outlined/>
            <Button
                type="button"
                disabled={loading || !isVatValid || isInvalidAllFields(newUserData)}
                label={__('Invia', 'htr')}
                icon="pi pi-check"
                iconPos="right"
                onClick={submitInvitation}/>
        </div>
    }, [isVatValid, loading, newUserData]);

    const submitInvitation = () => {
        /*storeSet('setAsyncRequest');

        AssignedApplicationService.assignApplication(submitInvitationCallback, errSubmitInvitationCallback, [
            ['userId', chosenUser]
        ]);*/
        hideInvitationDialog();
    }

    const submitInvitationCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: resp.message
                });
            }
        }
        storeSet('unsetAsyncRequest');
    }

    const errSubmitInvitationCallback = (resp) => {
        if (toast.current && resp.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: resp.message
            });
        }
        set404FromErrorResponse(resp);
        storeSet('unsetAsyncRequest');
    }

    const onChangeEditItem = (value, key) => {
        setNewUserData((prev) => {
            const userData = klona(prev);
            userData[key] = value;
            if (key === 'vatNumber') {
                setInputPiva(value);
            }
            return userData;
        });
    }

    const isInvalidField = (data, key) => key === 'vatNumber'
        ? !isPIVA(data[key])
        : isEmpty(data[key]) || isNil(data[key]);

    const isInvalidAllFields = (data) => [
        'vatNumber', 'firstName', 'lastName', 'companyName', 'categories'
    ].filter(k => isInvalidField(data, k)).length !== 0;

    const getStatValue = (key, fallback = '') => {
        return pathOr(fallback, [key], mainStats);
    }

    const getStats = (data) => {
        if (data.status === 'SUCCESS') {
            setMainStats(data.data);
        }
    }

    const errGetStats = () => {}

    const checkVatNumber = (value) => {
        if (errorMsgs.current) {
            errorMsgs.current.clear();
        }
        const isValid = isPIVA(value);

        if (isValid) {
            setLoading(true);
            CompanyService.checkVat(checkVatCallback, errCheckVatCallback, [
                ['vatNumber', value],
                ['alreadyExistsInDb', true]
            ])
        }
    }

    const checkVatCallback = (data) => {
        if (data.status === 'SUCCESS') {
            if (!data.data.valid) {
                setIsVatValid(false);
                errorMsgs.current.show([
                    {
                        sticky: true, severity: 'error', summary: '',
                        detail: sprintf(__('%s', 'htr'), data.data.message),
                        closable: true
                    }
                ]);
            } else {
                setIsVatValid(true);
            }

            const version = data.data.version;
            const resp = data.data?.vatCheckResponse?.data;
            if (resp && !isEmpty(resp)) {
                if (version === 'V2') {
                    const firstItem = resp[0];
                    const { companyName } = firstItem;
                    onChangeEditItem(companyName, 'companyName');
                }
                setVatCheckResponse(data.data.vatCheckResponse);
            }
        }

        setLoading(false);
    }

    const errCheckVatCallback = (resp) => {
        set404FromErrorResponse(resp);
        setLoading(false);
    }

    const handleSelectionCategories = (ids) => {
        setNewUserData(prev => ({...prev, categories: ids }));
    };

    const renderHeader = () => {
        return (
            <span className="ql-formats">
                <button className="ql-bold" aria-label="Bold"></button>
                <button className="ql-italic" aria-label="Italic"></button>
                <button className="ql-underline" aria-label="Underline"></button>
                <button className="ql-link" aria-label="Link"></button>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-header" value="2"></button>
                <button className="ql-header" value="3"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-indent" value="-1"></button>
                <button className="ql-indent" value="+1"></button>
            </span>
        );
    };

    const header = renderHeader();

    useEffect(() => {
        checkVatNumber(debouncedPivaValue);
    }, [debouncedPivaValue])

    useEffect(() => {
        if (roleIds.length > 0) {
            setLoading(true);
            UserService.getUsers(getUsersCallback, errGetUsersCallback, [['roleIds', roleIds]])
        }
    }, [roleIds]);

    useEffect(() => {
        if (isVisibleEditDialog) {
            setLoading(true);
            UserService.getRoles(getRolesCallback, errGetRolesCallback)
        }
    }, [isVisibleEditDialog]);

    useEffect(() => {
        DashboardService.getApplicationsStats(getStats, errGetStats);
    }, []);

    return (
        <div className="appPage">
            <div className="appPage__pageHeader">
                <h1>{__('Gestione fornitori', 'htr')}</h1>
            </div>

            <div className="appPage__spacer"></div>

            <div className="appPageSection">
                <Button
                    type="button"
                    disabled={loading}
                    icon="pi pi-plus"
                    iconPos="left"
                    label={__('Agguingi fornitore', 'htr')} onClick={openInvitationDialog}/>
            </div>

            <div className="appPage__spacer"></div>

            <div className="appPageSection">
                <AllDomandeTableAsync openDialogFn={openAssignDialog} updaterString={updaterString}/>
            </div>

            <div className="appPage__spacer"></div>

            <div className="appPageSection statsBigBadges">
                <h2>{__('Riepilogo', 'htr')}</h2>
                <div className="statsBigBadges__grid applStats">
                    <div className="statsBigBadges__gridItem">
                        <span>{__('Nuova Documentazione Ricevuta', 'htr')}</span>
                        <span><NumberFlow
                            value={getStatValue('numberOfApplication', 0)}
                            format={{ notation: 'compact' }}
                            locales="it-IT"/></span>
                    </div>
                    <div className="statsBigBadges__gridItem">
                        <span>{__('Assegnate', 'htr')}</span>
                        <span><NumberFlow
                            value={getStatValue('numberOfAssignedApplication', 0)}
                            format={{ notation: 'compact' }}
                            locales="it-IT"/></span>
                    </div>
                    <div className="statsBigBadges__gridItem">
                        <span>{__('Completate', 'htr')}</span>
                        <span><NumberFlow
                            value={getStatValue('numberOfAcceptedApplication', 0)}
                            format={{ notation: 'compact' }}
                            locales="it-IT"/></span>
                    </div>
                    <div className="statsBigBadges__gridItem">
                        <span>{__('Tempo Medio di Valutazione', 'htr')}</span>
                        <span><NumberFlow
                            value={getStatValue('evaluationAverageTime', 0)}
                            format={{ notation: 'compact' }}
                            suffix={` ${__('giorni', 'htr')}`}
                            locales="it-IT"/></span>
                    </div>
                    <div className="statsBigBadges__gridItem">
                        <span>{__('In richiesta di integrazioni', 'htr')}</span>
                        <span><NumberFlow
                            value={getStatValue('numberOfApplicationInAmendmentState', 0)}
                            format={{ notation: 'compact' }}
                            locales="it-IT"/></span>
                    </div>
                    <div className="statsBigBadges__gridItem">
                        <span>{__('Documentazione in Scadenza (48h)', 'htr')}</span>
                        <span><NumberFlow
                            value={getStatValue('numberOfDueApplication', 0)}
                            format={{ notation: 'compact' }}
                            locales="en-US"/></span>
                    </div>
                </div>
            </div>

            <Toast ref={toast}/>

            <Dialog
                visible={isVisibleEditDialog}
                modal
                header={headerEditDialog}
                footer={footerEditDialog}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideEditDialog}>
                <div className="appForm__field">
                    <label
                        className={classNames({ 'p-error': isEmpty(chosenUser) || chosenUser === 0 || chosenApplication === 0 })}>
                        {__('Operatore HTR', 'htr')}*
                    </label>
                    <Dropdown
                        value={chosenUser}
                        invalid={isEmpty(chosenUser) || chosenUser === 0 || chosenApplication === 0}
                        onChange={(e) => setChosenUser(e.value)}
                        options={users}
                        optionLabel="name"
                        optionValue="value"/>
                </div>
            </Dialog>

            <Dialog
                visible={isVisibleInvitationDialog}
                modal
                header={headerInvitationDialog}
                footer={footerInvitationDialog}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideInvitationDialog}>
                <div className="appForm__cols">
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isInvalidField(newUserData, 'firstName') })}>
                            {__('Nome', 'htr')}*
                        </label>
                        <InputText value={newUserData.firstName || ''}
                                   invalid={isInvalidField(newUserData, 'firstName')}
                                   onChange={(e) => onChangeEditItem(e.target.value, 'firstName')}/>
                    </div>
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isInvalidField(newUserData, 'lastName') })}>
                            {__('Cognome', 'htr')}*
                        </label>
                        <InputText value={newUserData.lastName || ''}
                                   invalid={isInvalidField(newUserData, 'lastName')}
                                   onChange={(e) => onChangeEditItem(e.target.value, 'lastName')}/>
                    </div>
                </div>
                <div className="appForm__cols">
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isEmpty(newUserData.email) || isNil(newUserData.email) || !isEmail(newUserData.email) })}>
                            {__('Email', 'htr')}*
                        </label>
                        <InputText value={newUserData.email || ''}
                                   invalid={isEmpty(newUserData.email) || isNil(newUserData.email) || !isEmail(newUserData.email)}
                                   onChange={(e) => onChangeEditItem(e.target.value, 'email')}/>
                    </div>
                    <div className="appForm__field">
                        <label>
                            {__('Telefono', 'htr')}
                        </label>
                        <InputText value={newUserData.phoneNumber || ''}
                                   keyfilter="int"
                                   onChange={(e) => onChangeEditItem(e.target.value, 'phoneNumber')}/>
                    </div>
                </div>
                <div className="appForm__cols">
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isInvalidField(newUserData, 'companyName') })}>
                            {__('Azienda', 'htr')}*
                        </label>
                        <InputText value={newUserData.companyName || ''}
                                   disabled={loading}
                                   invalid={isInvalidField(newUserData, 'companyName')}
                                   onChange={(e) => onChangeEditItem(e.target.value, 'companyName')}/>
                    </div>
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isInvalidField(newUserData, 'vatNumber') })}>
                            {__('Partita IVA', 'htr')}*
                        </label>
                        <InputText value={newUserData.vatNumber || ''}
                                   disabled={loading}
                                   invalid={isInvalidField(newUserData, 'vatNumber')}
                                   onChange={(e) => onChangeEditItem(e.target.value, 'vatNumber')}/>
                    </div>
                </div>
                <Messages ref={errorMsgs}/>
                <div className="appForm__field">
                    <ChipsStandalone
                        value={newUserData.categories || []}
                        multiple={false}
                        isRequired={true}
                        label={__('Categoria', 'htr')}
                        onChange={handleSelectionCategories}
                        disabled={loading}
                    />
                </div>
                <div className="appForm__field">
                    <label>
                        {__('Messaggio (opzionale)', 'htr')}
                    </label>
                    <div translate="no">
                        <Editor
                            value={newUserData.message}
                            readOnly={false}
                            placeholder={__('Digita qui il messagio', 'htr')}
                            headerTemplate={header}
                            onTextChange={(e) => onChangeEditItem(e.htmlValue, 'message')}
                            style={{ height: 80 * 3, width: '100%' }}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

export default Domande;